import { DOCUMENT } from '@angular/common';
import { Injectable, RendererFactory2, Signal, inject, signal } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class ScrollToTopObserverService {
    public readonly isAtTop: Signal<boolean>;

    #isAtTop = signal(true);
    #sentinel: HTMLElement | null = null;
    #observer: IntersectionObserver | null = null;

    readonly #document = inject(DOCUMENT);
    readonly #renderer = inject(RendererFactory2).createRenderer(null, null);

    constructor() {
        this.isAtTop = this.#isAtTop.asReadonly();

        this.setupIntersectionObserver();
    }

    private setupIntersectionObserver(): void {
        this.setupSentinel();
        this.createObserver();
        this.startObserving();
    }

    private setupSentinel(): void {
        if (this.#sentinel) return;

        this.#sentinel = this.#renderer.createElement('div');

        this.setSentinelStyles();

        this.#renderer.appendChild(this.#document.body, this.#sentinel);
    }

    private setSentinelStyles(): void {
        if (!this.#sentinel) return;

        this.#renderer.setStyle(this.#sentinel, 'position', 'absolute');
        this.#renderer.setStyle(this.#sentinel, 'top', '0');
        this.#renderer.setStyle(this.#sentinel, 'left', '0');
        this.#renderer.setStyle(this.#sentinel, 'z-index', '-1');
        this.#renderer.setStyle(this.#sentinel, 'pointer-events', 'none');
        this.#renderer.setAttribute(this.#sentinel, 'data-scroll-sentinel', 'true');
    }

    private createObserver(): void {
        if (this.#observer) return;

        this.#observer = new IntersectionObserver((entries) => this.handleIntersection(entries), {
            threshold: 0,
            rootMargin: '0px 0px 0px 0px',
        });
    }

    private handleIntersection(entries: IntersectionObserverEntry[]): void {
        const sentinelEntry = entries.find((entry) => entry.target === this.#sentinel);

        if (!sentinelEntry) return;

        this.#isAtTop.set(sentinelEntry.isIntersecting);
    }

    private startObserving(): void {
        if (!this.#observer || !this.#sentinel) return;

        this.#observer.observe(this.#sentinel);
    }
}
